<template>
    <div class="absolute top-0 w-full h-app-bar-bc">
        <div
            ref="mobileBCRef"
            class="relative h-app-bar-bc flex bg-white dark:bg-blue-800 border-gray-100 dark:border-blue-720 transition-all duration-500"
            :class="breadCrumbsStore.showMobileBC && isMbView ? 'top-app-bar max-mob:top-app-bar-mobile border-b' : '-top-app-bar'"
        >
            <CustomBreadCrumbs />
        </div>
    </div>

    <div
        class="absolute pr-5 max-mob:pr-4 w-full h-app-bar max-mob:h-app-bar-mobile bg-white dark:bg-blue-800 border-b border-gray-100 dark:border-gray-760"
        :class="wrapperClass"
    >
        <div class="flex flex-nowrap max-mob:flex-wrap items-center gap-x-7 max-mob:gap-x-4 w-full h-full">
            <div
                class="relative h-app-bar flex items-center grow truncate order-1"
                ref="headerRef"
            >
                <div
                    class="h-full flex items-center cursor-pointer shrink-0"
                    @click="showSideBars"
                >
                    <MenuIcon class="ml-4 text-gray-370 dark:text-white block min-mb:hidden" />
                </div>

                <CustomBreadCrumbs class="hidden min-mb:block" />
            </div>

            <div class="order-2">
                <HeaderDownloadStatus
                    :showDownloadPopup="showDownloadPopup"
                    @changeShowDownloadPopup="changeShowDownloadPopup"
                />
            </div>

            <div class="flex items-center gap-x-7 max-mob:gap-x-4 order-3 max-mob:order-last max-mob:w-full max-mob:py-4 max-mob:pl-4">
                <AccountBalance
                    v-if="isManager || viewBillingPermission"
                    :workspace="workspace"
                />

                <div class="flex items-center gap-x-2">
                    <div class="text-xs text-gray-370 dark:text-white">
                        Live:
                    </div>

                    <div class="text-xs text-green-600 dark:text-green">
                        {{ liveCalls }}
                    </div>
                </div>

                <div class="flex items-center gap-x-2">
                    <div class="text-xs text-gray-370 dark:text-white">
                        Total:
                    </div>

                    <div class="text-xs text-purple">
                        {{ totalCalls }}
                    </div>
                </div>
            </div>

            <div class="flex items-center gap-x-4 order-4">
                <AiAssistantBtn v-if="!isAccountTrial && isManager" />

                <HeaderHelpMenu />

                <div>
                    <NotificationMenu
                        v-if="!isAccountTrialEnded"
                        v-model="notificationMenu"
                        :smallNotificationMenu="smallNotificationMenu"
                        :unreadNotifications="unreadNotifications"
                        :readNotifications="readNotifications"
                        :loadingRead="notificationsStore.loadingRead"
                        :loadingReadIds="notificationsStore.loadingReadIds"
                        @onRead="notificationsStore.onRead"
                    />

                    <NotificationSmallMenu
                        v-if="!isAccountTrialEnded && unreadNotifications.length"
                        v-model="smallNotificationMenu"
                        :notifications="unreadNotifications"
                        @onOpenNotificationMenu="handleNotificationMenu"
                    />

                    <NotificationPopup
                        v-model="showPopup"
                        :notification="lastUrgentlyNotification"
                        @onRead="notificationsStore.onRead"
                    />
                </div>

                <RouterLink
                    :to="settingsTo"
                    class="w-8 h-8 flex items-center justify-center rounded-1.25 border border-transparent dark:border-blue-760 bg-purple-70 dark:bg-blue-780 text-gray-370 dark:text-white hover:bg-purple-50 dark:hover:bg-blue-770 transition-all cursor-pointer"
                >
                    <SettingsGearIcon />
                </RouterLink>
            </div>
        </div>

        <TrialVersionInfo
            v-if="isAccountTrial"
            :showMobileBC="breadCrumbsStore.showMobileBC && isMbView"
        />
    </div>
</template>

<script setup>
import {
    computed, ref, watch, inject,
} from 'vue'

import AccountBalance from '@/components/accountBalance/AccountBalance.vue'
import CustomBreadCrumbs from '@/components/breadCrumbs/CustomBreadCrumbs.vue'
import TrialVersionInfo from '@/components/trialVersionInfo/TrialVersionInfo.vue'
import NotificationMenu from '@/components/menu/NotificationMenu.vue'
import NotificationSmallMenu from '@/components/menu/NotificationSmallMenu.vue'
import MenuIcon from '@/components/icons/MenuIcon.vue'
import HeaderDownloadStatus from '@/components/download/HeaderDownloadStatus.vue'
import NotificationPopup from '@/components/notifications/NotificationPopup.vue'
import HeaderHelpMenu from '@/components/navigation/AppBar/HeaderHelpMenu.vue'
import SettingsGearIcon from '@/components/icons/SettingsGearIcon.vue'
import AiAssistantBtn from '@/components/ai/AiAssistantBtn.vue'

import { useBreadCrumbsStore } from '@/store/breadCrumbsStore'
import { useAuthorizationStore } from '@/store/authorizationStore'
import { useNotificationsStore } from '@/store/notificaitonsStore'
import { usePermissionsStore } from '@/store/permissionsStore'
import { useHeaderMetricsStore } from '@/store/headerMetricsStore'

import { viewPorts } from '@/constants/viewPorts'
import { ROUTES } from '@/constants/routes'

const props = defineProps({
    mini: {
        type: Boolean,
        default: false,
    },
    showDownloadPopup: {
        type: Boolean,
        default: false,
    },
    marginClass: {
        type: Array,
        default: () => [],
    },
})

const emits = defineEmits(['showSideBars', 'changeShowDownloadPopup'])

const notificationsStore = useNotificationsStore()
const breadCrumbsStore = useBreadCrumbsStore()
const authorizationStore = useAuthorizationStore()
const permissionsStore = usePermissionsStore()
const headerMetricsStore = useHeaderMetricsStore()

const currentViewSize = inject('currentViewSize')

const liveCalls = computed(() => headerMetricsStore.liveCalls)
const totalCalls = computed(() => headerMetricsStore.totalCalls)

const notificationMenu = ref(false)

const wrapperClass = computed(() => [
    ...props.marginClass,
    props.mini && 'min-mb:w-app-bar-min',
    !props.mini && 'min-mb:w-app-bar-max',
])

const smallNotificationMenu = computed({
    get() {
        return notificationsStore.smallNotificationMenu
    },
    set(value) {
        notificationsStore.changeSmallNotificationMenu(value)
    },
})

const isMbView = computed(() => viewPorts.mb >= currentViewSize.value)

const workspace = computed(() => authorizationStore.workspace)
const user = computed(() => authorizationStore.user)
const isAccountTrial = computed(() => authorizationStore.isAccountTrial)
const isAccountTrialEnded = computed(() => authorizationStore.isAccountTrialEnded)
const isManager = computed(() => authorizationStore.isManager)

const viewBillingPermission = computed(() => permissionsStore.permissions.billing.view)

const unreadNotifications = computed(() => notificationsStore.unreadNotifications)
const readNotifications = computed(() => notificationsStore.readNotifications)
const lastUrgentlyNotification = computed(() => notificationsStore.lastUnreadUrgentlyNotification)

const showPopup = ref(false)

function showSideBars() {
    emits('showSideBars')
}

const settingsTo = computed(() => {
    if (!authorizationStore.isOwner) {
        return { name: ROUTES.accountDetails }
    }

    if (!authorizationStore.isWorkspaceOwner) {
        return { name: ROUTES.accountDetails }
    }

    return {
        name: ROUTES.manageWorkspace,
        params: {
            account: authorizationStore.getAccountSlug,
            workspace: authorizationStore.getWorkspaceSlug,
        },
    }
})

function changeShowDownloadPopup() {
    emits('changeShowDownloadPopup')
}

function handleNotificationMenu(value) {
    notificationMenu.value = value
}

watch(lastUrgentlyNotification, () => {
    showPopup.value = !!lastUrgentlyNotification.value
}, { immediate: true })

watch(user, (user) => {
    if (user && !isAccountTrialEnded.value) {
        notificationsStore.loadNotifications()
    }
}, { immediate: true })
</script>
